import auth0 from "auth0-js";
import { keyConstants, apiEndPointConstants, path } from "../constants";
import HTTPService from "../utility/httpService";
import Utils from "../utility";

const { OAUTH_TOKEN } = apiEndPointConstants;
const { TOKEN, REFRESH_TOKEN } = keyConstants;

export default class Auth0Service {
  constructor(token) {
    this.httpService = new HTTPService(
      process.env.REACT_APP_AUTH0_BASEURL,
      {},
      true
    );
    this.config = {
      headers: {
        Authorization: `Bearer ${
          token !== undefined ? token : Utils.cookieGetItem(TOKEN)?.access_token
        }`
      }
    };
    this.auth0 = new auth0.WebAuth({
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      clientID: process.env.REACT_APP_AUTH0_CLIENT,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      redirectUri:
        window.location.pathname == path.EMAIL_VERIFICATION
          ? window.location.origin
          : window.location.href,
      scope: process.env.REACT_APP_AUTH0_SCOPE,
      responseType: process.env.REACT_APP_AUTH0_RESPONSE_TYPE,
      grantType: process.env.REACT_APP_AUTH0_GRANT_TYPE
    });
  }

  refreshAccessToken() {
    const refreshToken = Utils.cookieGetItem(TOKEN)?.refresh_token;
    try {
      const data = {
        grant_type: REFRESH_TOKEN,
        client_id: process.env.REACT_APP_AUTH0_CLIENT,
        client_secret: process.env.REACT_APP_AUTH0_CLIENT_SECRET,
        refresh_token: refreshToken
      };
      const response = this.httpService.post(OAUTH_TOKEN, data, this.config);
      return response;
    } catch (error) {
      console.error("Error fetching token:", error);
      return error;
    }
  }

  /**
   * Signs up a user with the provided name, email, and password.
   *
   * @param {object} params - The parameters for signing up a user.
   * @param {string} params.name - The name of the user.
   * @param {string} params.email - The email address of the user.
   * @param {string} params.password - The password for the user.
   * @return {Promise} A promise that resolves with the authentication result or rejects with an error.
   */
  signUp({ name, email, password }) {
    return new Promise((resolve, reject) => {
      this.auth0.signup(
        {
          connection: process.env.REACT_APP_AUTH0_REALM,
          realm: process.env.REACT_APP_AUTH0_REALM,
          email: email.toLowerCase(),
          password: password,
          given_name: name,
          name: name,
          nickname: name,
          grantType: process.env.REACT_APP_AUTH0_GRANT_TYPE,
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        },
        (err, authResult) => {
          if (err) return reject(err);
          return resolve(authResult);
        }
      );
    });
  }

  /**
   * Sign in a user with the given email and password.
   *
   * @param {object} email - The user's email.
   * @param {string} password - The user's password.
   * @return {Promise} A promise that resolves to the authentication result.
   */
  signIn({ email, password }) {
    return new Promise((resolve, reject) => {
      this.auth0.login(
        {
          realm: process.env.REACT_APP_AUTH0_REALM,
          username: email.toLowerCase(),
          password: password,
          clientID: process.env.REACT_APP_AUTH0_CLIENT,
          connection: process.env.REACT_APP_AUTH0_REALM,
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        },
        (err, authResult) => {
          if (err) return reject(err);
          return resolve(authResult);
        }
      );
    });
  }

  /**
   * Executes the `userInfo` function, which returns a promise that resolves
   * with the user information obtained from the Auth0 client. The function
   * first parses the hash received from the authentication callback and then
   * retrieves the user information using the obtained access token.
   *
   * @return {Promise<object>} A promise that resolves with the user information
   * if successful, or rejects with an error if any error occurs during the
   * process.
   */
  userInfo() {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash((error, authResult) => {
        if (error) return reject(error);
        this.auth0.client.userInfo(
          authResult.accessToken,
          (userError, user) => {
            if (userError) return reject(userError);
            resolve(user);
          }
        );
      });
    });
  }

  /**
   * Retrieves a token for authentication.
   *
   * @param {object} obj - The object containing email, password, and audience.
   * @return {Promise} A Promise that resolves with the retrieved token or rejects with an error.
   */
  getToken = async (obj) => {
    try {
      const payload = {
        grant_type: process.env.REACT_APP_AUTH0_GRANT_TYPE,
        username: obj?.email.toLowerCase(),
        password: obj?.password,
        client_id: process.env.REACT_APP_AUTH0_CLIENT,
        client_secret: process.env.REACT_APP_AUTH0_CLIENT_SECRET,
        connection: process.env.REACT_APP_AUTH0_REALM,
        realm: process.env.REACT_APP_AUTH0_REALM,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: process.env.REACT_APP_AUTH0_SCOPE
      };

      const response = await this.httpService.post(
        OAUTH_TOKEN,
        payload,
        this.config
      );
      return response;
    } catch (error) {
      console.error("Error fetching token:", error);
      return error;
    }
  };
}
